/**
 * Ficha de un pedido. Sustituye al modal: se abre, se recarga y se comparte
 * por /store/orders/:id. La vuelta al listado conserva la búsqueda.
 */

import { useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Copy, Download, Loader2, RefreshCw, Truck } from 'lucide-react';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { ResourceDetail } from '@/components/shared/ResourceDetail';
import { Button } from '@/components/ui/button';
import { formatDateTime, formatEurFromCents } from '@/lib/format-es';
import { notifyErr, notifyOk } from '@/lib/notify';
import { storeOrdersListHrefFrom } from '@/lib/store-orders-return';
import { downloadLegalInvoicePdf } from '@/pages/billing/hooks/useLegalInvoices';
import { StoreOrderStatusBadge } from './components';
import {
    useCancelStoreOrderLine,
    useRefundStoreOrder,
    useResendStoreOrderEmails,
    useStoreOrderDetail,
    useUpdateStoreOrder,
} from './hooks/useStoreOrders';
import {
    expeditionSummary,
    groupLinesByOrigin,
    orderGoodsCostCents,
    shippingZoneLabel,
    SUPPLIER_MANUAL_NOTE,
    supplierOrderClipboard,
    type OriginGroup,
} from './orderDetailModel';
import { StripeModeNotice } from '../settings/StripeModeNotice';
import { useStripeMode } from '../settings/useStripeMode';
import { statusLabel } from './orderStatus';
import type { StoreOrderDetail, StoreOrderStatus } from './types';

type ConfirmKind = 'processing' | 'shipped' | 'delivered' | 'completed' | 'cancel' | 'refund' | 'resend';

const RESEND_STATUSES = new Set<StoreOrderStatus>(['paid', 'processing', 'shipped', 'delivered']);

function eur(cents: number): string {
    return formatEurFromCents(cents || 0);
}

function SideCard({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="rounded-xl border border-border bg-card p-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
            <div className="space-y-1 text-sm text-foreground">{children}</div>
        </section>
    );
}

function AmountRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums">{value}</span>
        </div>
    );
}

export function StoreOrderDetailPage() {
    const { orderId = null } = useParams();
    const location = useLocation();
    const listHref = storeOrdersListHrefFrom(location.state);
    const detail = useStoreOrderDetail(orderId);
    const updateOrder = useUpdateStoreOrder();
    const refundOrder = useRefundStoreOrder();
    const cancelLine = useCancelStoreOrderLine();
    const resendEmails = useResendStoreOrderEmails();
    const stripeMode = useStripeMode();
    const order = detail.data ?? null;

    const [trackingCarrier, setTrackingCarrier] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [trackingUrl, setTrackingUrl] = useState('');
    const [showShippedForm, setShowShippedForm] = useState(false);
    const [cancellationReason, setCancellationReason] = useState('');
    const [showCancelForm, setShowCancelForm] = useState(false);
    const [refundReason, setRefundReason] = useState('');
    const [showRefundForm, setShowRefundForm] = useState(false);
    const [returned, setReturned] = useState<Record<string, boolean>>({});
    const [lineToCancel, setLineToCancel] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<ConfirmKind | null>(null);
    const [invoiceDownloading, setInvoiceDownloading] = useState(false);

    const busy = updateOrder.isPending || refundOrder.isPending || resendEmails.isPending || cancelLine.isPending;
    const groups = order ? groupLinesByOrigin(order.items) : [];
    const goodsCost = order ? orderGoodsCostCents(order.items) : null;

    const fail = (error: unknown) => {
        notifyErr(error instanceof Error ? error.message : 'No se ha podido completar la acción');
    };

    const submitStatus = (
        newStatus: 'processing' | 'shipped' | 'delivered' | 'completed' | 'cancelled',
        extra: Record<string, string | undefined> = {},
    ) => {
        if (!order) return;
        updateOrder.mutate(
            { orderId: order.id, newStatus, ...extra },
            {
                onSuccess: () => {
                    notifyOk('Pedido actualizado');
                    setConfirm(null);
                    setShowShippedForm(false);
                    setShowCancelForm(false);
                },
                onError: fail,
            },
        );
    };

    const confirmAction = () => {
        if (!order || !confirm) return;
        if (confirm === 'processing') submitStatus('processing');
        else if (confirm === 'shipped') {
            submitStatus('shipped', {
                trackingCarrier: trackingCarrier.trim(),
                trackingNumber: trackingNumber.trim(),
                trackingUrl: trackingUrl.trim() || undefined,
            });
        } else if (confirm === 'delivered') submitStatus('delivered');
        else if (confirm === 'completed') submitStatus('completed');
        else if (confirm === 'cancel') {
            submitStatus('cancelled', { cancellationReason: cancellationReason.trim() || undefined });
        } else if (confirm === 'refund') {
            const needsReturn = ['shipped', 'delivered', 'completed'].includes(order.status);
            const merchLines = order.items.filter((line) => line.variantId && line.kind !== 'catalog');
            refundOrder.mutate(
                {
                    orderId: order.id,
                    reason: refundReason.trim() || undefined,
                    returns: needsReturn
                        ? merchLines.map((line) => ({ itemId: line.id, returned: returned[line.id] === true }))
                        : undefined,
                },
                {
                    onSuccess: () => {
                        notifyOk('Reembolso solicitado. El estado se actualizará al llegar el webhook de Stripe.');
                        setConfirm(null);
                        setShowRefundForm(false);
                    },
                    onError: fail,
                },
            );
        } else if (confirm === 'resend') {
            resendEmails.mutate(order.id, {
                onSuccess: () => {
                    notifyOk('Emails reenviados');
                    setConfirm(null);
                },
                onError: fail,
            });
        }
    };

    const copySupplierOrder = async (group: OriginGroup) => {
        if (!order) return;
        const text = supplierOrderClipboard({
            orderNumber: order.orderNumber,
            lines: group.lines,
            address: order.shippingAddress,
            phone: order.customerPhone,
        });
        try {
            await navigator.clipboard.writeText(text);
            notifyOk('Pedido copiado');
        } catch {
            notifyErr('No se ha podido copiar el pedido');
        }
    };

    const downloadInvoice = async (current: StoreOrderDetail) => {
        if (!current.legalInvoiceId) return;
        setInvoiceDownloading(true);
        try {
            await downloadLegalInvoicePdf(current.legalInvoiceId, current.legalInvoiceNumber ?? 'factura');
        } catch (error) {
            fail(error);
        } finally {
            setInvoiceDownloading(false);
        }
    };

    if (detail.isLoading) {
        return (
            <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="h-16 animate-pulse rounded-xl bg-muted" />
                ))}
            </div>
        );
    }

    if (detail.isError || !order) {
        return (
            <div className="space-y-3">
                <p className="text-sm text-red-400">
                    {detail.isError ? 'No se ha podido cargar el pedido.' : 'Este pedido no existe.'}
                </p>
                <Link to={listHref} className="text-sm text-primary hover:underline">
                    Volver a pedidos
                </Link>
            </div>
        );
    }

    const unsavedMessage = showShippedForm
        ? 'El seguimiento todavía no se ha guardado.'
        : showCancelForm
          ? 'La cancelación todavía no se ha confirmado.'
          : showRefundForm
            ? 'El reembolso todavía no se ha confirmado.'
            : null;

    const wallet = order.walletAppliedCents ?? 0;

    return (
        <>
            <ResourceDetail
                crumbs={[
                    { label: 'Pedidos', to: listHref },
                    { label: order.orderNumber },
                ]}
                title={`Pedido ${order.orderNumber}`}
                badge={
                    <div className="flex items-center gap-2">
                        <StoreOrderStatusBadge status={order.status} />
                        <span className="text-xs text-muted-foreground">{formatDateTime(order.createdAt)}</span>
                    </div>
                }
                unsavedMessage={unsavedMessage}
                actions={
                    <>
                        {order.status === 'paid' ? (
                            <Button type="button" size="sm" disabled={busy} onClick={() => setConfirm('processing')}>
                                Marcar en preparación
                            </Button>
                        ) : null}
                        {order.status === 'processing' ? (
                            <Button type="button" size="sm" disabled={busy} onClick={() => setShowShippedForm(true)}>
                                <Truck /> Marcar como enviado
                            </Button>
                        ) : null}
                        {order.status === 'shipped' ? (
                            <Button type="button" size="sm" disabled={busy} onClick={() => setConfirm('delivered')}>
                                Marcar como entregado
                            </Button>
                        ) : null}
                        {['paid', 'processing', 'shipped', 'delivered'].includes(order.status) ? (
                            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setConfirm('completed')}>
                                Marcar como completado
                            </Button>
                        ) : null}
                        {['pending_payment', 'paid', 'processing'].includes(order.status) ? (
                            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setShowCancelForm(true)}>
                                Cancelar pedido
                            </Button>
                        ) : null}
                        {['paid', 'processing', 'shipped', 'delivered', 'completed'].includes(order.status) ? (
                            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setShowRefundForm(true)}>
                                <RefreshCw /> Reembolsar
                            </Button>
                        ) : null}
                        {RESEND_STATUSES.has(order.status) ? (
                            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => setConfirm('resend')}>
                                Reenviar emails
                            </Button>
                        ) : null}
                    </>
                }
                aside={
                    <>
                        <SideCard title="Cliente">
                            <div className="font-medium">{order.customerName}</div>
                            <div className="text-muted-foreground">{order.customerEmail}</div>
                            <div className="text-muted-foreground">{order.customerPhone}</div>
                            {order.customerTaxId ? (
                                <div className="text-xs text-muted-foreground">NIF/CIF: {order.customerTaxId}</div>
                            ) : null}
                            {order.customerNotes ? <p className="pt-1 text-muted-foreground">{order.customerNotes}</p> : null}
                        </SideCard>
                        <SideCard title="Envío">
                            <AmountRow label="Zona" value={shippingZoneLabel(order.shippingRateLabel)} />
                            <AmountRow label="Tarifa" value={order.shippingCents === 0 ? 'Gratis' : eur(order.shippingCents)} />
                            <div className="pt-2 text-muted-foreground">
                                {order.shippingAddress.fullName}
                                <br />
                                {order.shippingAddress.line1}
                                {order.shippingAddress.line2 ? (
                                    <>
                                        <br />
                                        {order.shippingAddress.line2}
                                    </>
                                ) : null}
                                <br />
                                {order.shippingAddress.postalCode} {order.shippingAddress.city}
                                <br />
                                {order.shippingAddress.country}
                            </div>
                            {order.trackingCarrier || order.trackingNumber ? (
                                <div className="pt-2">
                                    <div className="font-medium">Seguimiento</div>
                                    <div className="text-muted-foreground">
                                        {order.trackingCarrier} · {order.trackingNumber}
                                    </div>
                                    {order.trackingUrl ? (
                                        <a href={order.trackingUrl} target="_blank" rel="noreferrer" className="break-all text-primary hover:underline">
                                            {order.trackingUrl}
                                        </a>
                                    ) : null}
                                </div>
                            ) : null}
                        </SideCard>
                        <SideCard title="Pago">
                            <AmountRow label="Subtotal" value={eur(order.subtotalCents)} />
                            <AmountRow label="Envío" value={order.shippingCents === 0 ? 'Gratis' : eur(order.shippingCents)} />
                            {wallet > 0 ? <AmountRow label="Saldo AiPeces" value={`−${eur(wallet)}`} /> : null}
                            <div className="border-t border-border pt-1 font-semibold">
                                <AmountRow label="Total" value={eur(order.totalCents)} />
                            </div>
                        </SideCard>
                        <details className="rounded-xl border border-border bg-card p-4">
                            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Avanzado
                            </summary>
                            <div className="space-y-1 pt-3 text-xs text-muted-foreground">
                                {order.stripePaymentIntentId ? (
                                    <div>PI: <code className="font-mono">{order.stripePaymentIntentId}</code></div>
                                ) : null}
                                {order.stripeCheckoutSessionId ? (
                                    <div>Session: <code className="font-mono">{order.stripeCheckoutSessionId}</code></div>
                                ) : null}
                                {!order.stripePaymentIntentId && !order.stripeCheckoutSessionId ? (
                                    <p>Sin identificadores de Stripe</p>
                                ) : null}
                            </div>
                        </details>
                        <SideCard title="Margen">
                            {goodsCost == null ? (
                                <p>
                                    <span className="tabular-nums">—</span>{' '}
                                    <span className="text-muted-foreground">sin coste cargado</span>
                                </p>
                            ) : (
                                <>
                                    <AmountRow label="Coste de las líneas" value={eur(goodsCost)} />
                                    <AmountRow label="Margen" value={eur(order.subtotalCents - goodsCost)} />
                                </>
                            )}
                        </SideCard>
                        <SideCard title="Factura">
                            {order.legalInvoiceId ? (
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="font-mono text-xs">{order.legalInvoiceNumber ?? order.legalInvoiceId}</span>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={invoiceDownloading}
                                        onClick={() => void downloadInvoice(order)}
                                    >
                                        {invoiceDownloading ? <Loader2 className="animate-spin" /> : <Download />}
                                        PDF
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-muted-foreground">Sin factura</p>
                            )}
                        </SideCard>
                    </>
                }
            >
                {groups.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Este pedido no tiene líneas.</p>
                ) : (
                    <>
                    <p className="text-sm text-muted-foreground">{expeditionSummary(groups)}</p>
                    {groups.map((group) => (
                        <section key={group.key} className="rounded-xl border border-border bg-card">
                            <header className="space-y-2 border-b border-border p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <h2 className="text-sm font-semibold text-foreground">{group.title}</h2>
                                    {group.kind === 'supplier' ? (
                                        <Button type="button" size="sm" variant="outline" onClick={() => void copySupplierOrder(group)}>
                                            <Copy /> Copiar pedido para el proveedor
                                        </Button>
                                    ) : null}
                                </div>
                                {group.kind === 'supplier' ? (
                                    <>
                                        <p className="text-sm text-muted-foreground">
                                            Email de pedidos:{' '}
                                            {group.orderContactEmail ?? 'sin email configurado'}
                                        </p>
                                        <p className="text-xs text-muted-foreground">{SUPPLIER_MANUAL_NOTE}</p>
                                    </>
                                ) : null}
                            </header>
                            <ul>
                                {group.lines.map((line) => (
                                    <li key={line.id} className="flex items-start gap-3 border-b border-border p-4 last:border-0">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-foreground">{line.productName}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {line.variantLabel ? `${line.variantLabel} · ` : ''}
                                                {line.supplierReference ? `Ref. ${line.supplierReference}` : line.sku}
                                                {line.ean ? ` · EAN ${line.ean}` : ''}
                                            </div>
                                        </div>
                                        <div className="text-sm tabular-nums text-foreground">×{line.quantity}</div>
                                        {group.kind === 'merch' && (order.status === 'paid' || order.status === 'processing') ? (
                                            <Button type="button" size="sm" variant="outline" onClick={() => setLineToCancel(line.id)}>
                                                Cancelar línea
                                            </Button>
                                        ) : null}
                                        <div className="w-28 text-right text-sm">
                                            <div className="font-medium tabular-nums text-foreground">{eur(line.lineTotalCents)}</div>
                                            <div className="text-xs tabular-nums text-muted-foreground">{eur(line.unitPriceCents)}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <div className="flex justify-between border-t border-border px-4 py-3 text-sm">
                                <span className="text-muted-foreground">Subtotal</span>
                                <span className="font-medium tabular-nums">{eur(group.subtotalCents)}</span>
                            </div>
                        </section>
                    ))}
                    </>
                )}

                {showShippedForm ? (
                    <section className="space-y-2 rounded-xl border border-border bg-card p-4">
                        <h2 className="text-sm font-semibold">Seguimiento</h2>
                        <input
                            value={trackingCarrier}
                            onChange={(event) => setTrackingCarrier(event.target.value)}
                            placeholder="Transportista (ej. SEUR, MRW, Correos)"
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        />
                        <input
                            value={trackingNumber}
                            onChange={(event) => setTrackingNumber(event.target.value)}
                            placeholder="Número de tracking"
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        />
                        <input
                            value={trackingUrl}
                            onChange={(event) => setTrackingUrl(event.target.value)}
                            placeholder="URL de seguimiento (opcional)"
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        />
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                size="sm"
                                disabled={busy || !trackingCarrier.trim() || !trackingNumber.trim()}
                                onClick={() => setConfirm('shipped')}
                            >
                                Confirmar envío
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => setShowShippedForm(false)}>
                                Volver
                            </Button>
                        </div>
                    </section>
                ) : null}

                {showCancelForm ? (
                    <section className="space-y-2 rounded-xl border border-border bg-card p-4">
                        <h2 className="text-sm font-semibold">Cancelar pedido</h2>
                        <textarea
                            value={cancellationReason}
                            onChange={(event) => setCancellationReason(event.target.value)}
                            placeholder="Motivo de la cancelación"
                            rows={3}
                            className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
                        />
                        <div className="flex gap-2">
                            <Button type="button" size="sm" variant="destructive" disabled={busy} onClick={() => setConfirm('cancel')}>
                                Confirmar cancelación
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => setShowCancelForm(false)}>
                                Volver
                            </Button>
                        </div>
                    </section>
                ) : null}

                {showRefundForm ? (
                    <section className="space-y-2 rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
                        <h2 className="text-sm font-semibold text-rose-400">Reembolsar vía Stripe</h2>
                        <StripeModeNotice mode={stripeMode.data} pending={stripeMode.isLoading} />
                        <p className="text-sm text-rose-400">
                            Se creará un reembolso completo en Stripe. El estado se actualizará automáticamente cuando llegue el webhook.
                        </p>
                        <textarea
                            value={refundReason}
                            onChange={(event) => setRefundReason(event.target.value)}
                            placeholder="Motivo del reembolso (interno)"
                            rows={2}
                            className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
                        />
                        {['shipped', 'delivered', 'completed'].includes(order.status) ? (
                            <div className="space-y-2">
                                <p className="text-sm">¿Ha vuelto la prenda al almacén?</p>
                                {order.items.filter((line) => line.variantId && line.kind !== 'catalog').map((line) => (
                                    <label key={line.id} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={returned[line.id] === true}
                                            onChange={(event) => setReturned((prev) => ({
                                                ...prev,
                                                [line.id]: event.target.checked,
                                            }))}
                                        />
                                        {line.productName}{line.variantLabel ? ` · ${line.variantLabel}` : ''}
                                    </label>
                                ))}
                                <p className="text-xs text-muted-foreground">
                                    Solo vuelve al stock la prenda que marques. El resto queda como reembolso sin devolución.
                                </p>
                            </div>
                        ) : (
                            <p className="text-xs text-muted-foreground">
                                El pedido aún no se ha enviado. Si el control de stock está activo, las prendas vuelven al almacén.
                            </p>
                        )}
                        <div className="flex gap-2">
                            <Button type="button" size="sm" variant="destructive" disabled={busy} onClick={() => setConfirm('refund')}>
                                Confirmar reembolso
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => setShowRefundForm(false)}>
                                Volver
                            </Button>
                        </div>
                    </section>
                ) : null}

                <section className="rounded-xl border border-border bg-card p-4">
                    <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Historial</h2>
                    {order.history.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Sin cambios de estado.</p>
                    ) : (
                        <ul className="space-y-2">
                            {order.history.map((entry, index) => (
                                <li key={`${entry.createdAt}-${index}`} className="flex flex-wrap items-center gap-3 text-sm">
                                    <span className="w-36 shrink-0 text-xs tabular-nums text-muted-foreground">
                                        {formatDateTime(entry.createdAt)}
                                    </span>
                                    <span>
                                        {entry.fromStatus ? `${statusLabel(entry.fromStatus)} → ` : ''}
                                        <strong>{statusLabel(entry.toStatus)}</strong>
                                    </span>
                                    {entry.note ? <span className="truncate text-muted-foreground italic">{entry.note}</span> : null}
                                </li>
                            ))}
                        </ul>
                    )}
                    {order.cancellationReason ? (
                        <p className="mt-3 text-sm text-muted-foreground">Motivo: {order.cancellationReason}</p>
                    ) : null}
                </section>
            </ResourceDetail>

            <AdminConfirmDialog
                open={confirm != null}
                variant={confirm === 'cancel' || confirm === 'refund' ? 'danger' : 'default'}
                title={
                    confirm === 'processing'
                        ? 'Marcar en preparación'
                        : confirm === 'shipped'
                          ? 'Marcar como enviado'
                          : confirm === 'delivered'
                            ? 'Marcar como entregado'
                            : confirm === 'completed'
                              ? 'Marcar como completado'
                              : confirm === 'cancel'
                                ? 'Cancelar pedido'
                                : confirm === 'refund'
                                  ? 'Reembolsar pedido'
                                  : 'Reenviar emails'
                }
                description={
                    confirm === 'shipped'
                        ? `Se guardará el seguimiento de ${trackingCarrier.trim()} (${trackingNumber.trim()}) y el pedido pasará a enviado.`
                        : confirm === 'refund'
                          ? 'Se creará un reembolso completo en Stripe. El estado del pedido lo actualizará el webhook.'
                          : confirm === 'cancel'
                            ? 'El pedido quedará cancelado. Esta acción no se deshace desde aquí.'
                            : confirm === 'resend'
                              ? 'Se reenviarán la confirmación al cliente y el aviso interno.'
                              : confirm === 'completed'
                                ? 'El pedido pasará a completado. Es distinto de entregado: no implica una entrega física nueva.'
                                : 'El estado del pedido cambiará y no se deshace desde esta pantalla.'
                }
                confirmLabel={
                    confirm === 'cancel' ? 'Cancelar pedido' : confirm === 'refund' ? 'Confirmar reembolso' : 'Confirmar'
                }
                confirmDisabled={busy}
                onConfirm={confirmAction}
                onCancel={() => setConfirm(null)}
            />
            <AdminConfirmDialog
                open={lineToCancel != null}
                variant="danger"
                title="Cancelar esta línea"
                description="La línea deja de prepararse. Si el control de stock está activo, esas unidades vuelven al almacén."
                confirmLabel="Cancelar línea"
                confirmDisabled={cancelLine.isPending}
                onConfirm={() => {
                    if (!lineToCancel) return;
                    cancelLine.mutate(
                        { itemId: lineToCancel },
                        {
                            onSuccess: () => {
                                notifyOk('Línea cancelada');
                                setLineToCancel(null);
                            },
                            onError: (error) => notifyErr(error instanceof Error ? error.message : String(error)),
                        },
                    );
                }}
                onCancel={() => setLineToCancel(null)}
            />
        </>
    );
}
